import { Injectable, inject } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

import { OrderKeys } from '@config/keys/order.keys';
import { IPaymentMethod } from '@config/payment-methods.config';
import { PaymentStoreRefiner } from '@common/refiners/payment.refiner';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { PaymentStoreSelector } from '@common/selectors/payment.selector';

import {
  PAYMENT_METHOD_PICKER_BLOCK_LOADING_VM,
  PaymentMethodPickerBlockDataSource,
  PaymentMethodPickerBlockViewModel,
} from './payment-method-picker-block-view-model';

@Injectable()
export class PaymentMethodPickerBlockStoreDataSource implements PaymentMethodPickerBlockDataSource {
  private readonly paymentRefiner = inject(PaymentStoreRefiner);
  private readonly paymentSelector = inject(PaymentStoreSelector);
  private readonly orderSelector = inject(OrderStoreSelector);

  /**
   * Status / validation updates must not remap payment methods: the Stripe panel
   * creates an order on init, so remounting the @for row would POST /orders in a loop.
   */
  readonly vm$: Observable<PaymentMethodPickerBlockViewModel> = combineLatest([
    this.paymentRefiner.selectPaymentMethodsWithDescriptors$.pipe(
      map(methods => this.mapAvailablePaymentMethods(methods)),
      distinctUntilChanged((prev, next) => this.arePaymentMethodsEquivalent(prev, next)),
    ),
    this.paymentSelector.selectStatus.pipe(
      map(status => status.isRetryInProgress),
      distinctUntilChanged(),
    ),
    this.orderSelector.selectValidation$.pipe(
      map(order => order?.[OrderKeys.FinalPrice] ?? null),
      distinctUntilChanged(),
    ),
  ]).pipe(
    map(([availablePaymentMethods, isRetryInProgress, orderFinalPrice]) => {
      if (!availablePaymentMethods.length) {
        return {
          ...PAYMENT_METHOD_PICKER_BLOCK_LOADING_VM,
          orderFinalPrice,
          isRetryInProgress,
        };
      }

      return {
        availablePaymentMethods,
        orderFinalPrice,
        remainingMinutes: null,
        isRetryInProgress,
      };
    }),
  );

  private mapAvailablePaymentMethods(
    methods: readonly IPaymentMethod[] | null | undefined,
  ): IPaymentMethod[] {
    const list = methods || [];
    return list.map(method => ({
      ...method,
      data: {
        ...method.data,
        isUnique: list.length === 1,
      },
    }));
  }

  private arePaymentMethodsEquivalent(
    prev: readonly IPaymentMethod[],
    next: readonly IPaymentMethod[],
  ): boolean {
    if (prev === next) {
      return true;
    }
    if (prev.length !== next.length) {
      return false;
    }
    return prev.every(
      (method, index) =>
        method.handle === next[index]?.handle &&
        method.label === next[index]?.label &&
        method.panelComponent === next[index]?.panelComponent,
    );
  }
}
